import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  ticketRemoteConnectQuery,
  ticketRemoteControlQuery,
  type TicketRemoteDevice,
  type TicketRemoteLinuxDevice,
} from '../api/easeedesk'
import { remoteDownloadUrl, sendAgentDownloadLink } from '../api/remoteConnect'
import { rustdeskLaunchUrl } from '../api/remoteConnectRustdesk'
import { rcUnviewedQuery } from '../api/easeedeskRemoteControl'
import { renderScreen } from '../devices/screens'
import { agoOrEmpty } from './format'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import { RemoteSessionsTable } from '../remote_connect/RemoteSessions'
import './TicketRemote.css'

/**
 * Cele doua file de acces la distanta: "Remote Connect" si "Remote Control".
 *
 * ALEGEREA e aici: ce masini are tichetul, care raspund acum, care sunt in
 * sediu, si ce panouri se pot deschide pe ele. PORNIREA e pagina de lansare
 * RustDesk (`rustdeskLaunchUrl`), in fila noua - Remote Connect in v4 e NUMAI
 * RustDesk (decizia din 12 septembrie 2026); ScreenConnect si rezerva lui,
 * EaseeRemote, nu mai exista.
 *
 * "Remote Control" iese intreg: panourile lui - lista din stanga, configurabila
 * pe furnizor - sunt CHIAR ecranele mutate ale fisei de aparat, deci se deschid
 * in pagina, pe masina aleasa din dreapta.
 */

/* ------------------------------------------------------------------------ */
/*  Remote Connect                                                           */
/* ------------------------------------------------------------------------ */

/*
 * Aceeasi asezare ca machetele portalului
 * (views/version-3/easeedesk/tickets/remote_connect/*.php): un "note" cand nu e
 * niciun aparat, un "note" cu butonul de SSH pe fiecare server Linux, si cate
 * un bs-callout gri pe fiecare statie cu agent - stanga semnul de comunicare,
 * iconita tipului, numele si utilizatorul logat; dreapta butoanele; dedesubt
 * avertismentul "in the office" si bifa de consimtamant.
 */

export function TicketRemoteConnect({ id }: { id: number }) {
  const { data, error } = useQuery(ticketRemoteConnectQuery(id))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  return (
    <div id={`rmt-${id}`} className="tk-rconnect">
      {data.devices.length === 0 && data.linuxDevices.length === 0 && (
        <div className="note note-info tk-rconnect-note">
          <i className="fas fa-info-circle" aria-hidden="true"></i> No available devices to connect.{' '}
          {data.affectedOwner !== null && data.affectedOwner > 0 && (
            <SendAgentLinkForm ticket={id} owner={data.affectedOwner} size="xs" />
          )}
        </div>
      )}

      {data.linuxDevices.map((device) => (
        <LinuxDevice key={device.id} device={device} />
      ))}
      {data.devices.map((device) => (
        <RemoteDevice key={device.id} ticket={id} device={device} />
      ))}

      {/* CEC 65: sesiunile pornite de pe tichetul asta, cu inregistrarea lor
          (redarea cere dreptul de inregistrari - il hotaraste serverul). */}
      <div className="rcs-ticket">
        <h4>
          <i className="fas fa-history" aria-hidden="true"></i> Remote sessions on this ticket
        </h4>
        <RemoteSessionsTable filter={{ ticket: id }} showTicket={false} />
      </div>
    </div>
  )
}

/**
 * Cele cinci trepte ale semnului de comunicare - aceleasi praguri ca
 * `Services\Device\Comm::TIERS`, desenate ca `IdentityMark::statusDot()`.
 */
function commState(lastActivity: string): { key: string; label: string } {
  const t = lastActivity && !lastActivity.startsWith('0000') ? Date.parse(lastActivity.replace(' ', 'T')) : NaN
  if (Number.isNaN(t)) return { key: 'lost', label: 'No word for more than 7 days' }
  const seconds = (Date.now() - t) / 1000
  if (seconds < 30) return { key: 'live', label: 'Communicating right now' }
  if (seconds < 3600) return { key: 'fresh', label: 'Communicated in the last hour' }
  if (seconds < 86400) return { key: 'ok', label: 'Communicated in the last 24 hours' }
  if (seconds < 604800) return { key: 'stale', label: 'No word for more than a day' }
  return { key: 'lost', label: 'No word for more than 7 days' }
}

function StatusDot({ lastActivity }: { lastActivity: string }) {
  const comm = commState(lastActivity)
  if (comm.key === 'stale' || comm.key === 'lost') {
    return (
      <span className={`od-comm od-comm-${comm.key} remote-devices-pulse tk-rconnect-dot`} title={comm.label}>
        <i className="fas fa-exclamation" aria-hidden="true"></i>
      </span>
    )
  }
  return (
    <span className={`dots od-comm od-comm-${comm.key} remote-devices-pulse tk-rconnect-dot`} title={comm.label}></span>
  )
}

/** Iconita tipului de aparat, ca `get_icon()` din devices_helper.php. */
const TYPE_ICONS: Record<number, string> = {
  1: 'desktop',
  2: 'laptop',
  3: 'server',
  4: 'print',
  6: 'wifi',
  11: 'mobile',
  12: 'tablet',
  55: 'hdd-o',
  71: 'keyboard-o',
  87: 'tv',
}

function TypeIcon({ type }: { type: number }) {
  const icon = TYPE_ICONS[type]
  return (
    <i
      className={`fas fa-${icon ?? 'question-circle'} font-18 ${icon ? '' : 'color-blue'} tk-rconnect-type`}
      aria-hidden="true"
    ></i>
  )
}

/**
 * Formularul pentru linkul de instalare a agentului
 * (`Remote_connect::sendAgentDownloadLink`, `sendAgentDownloadLinkForm` din
 * easeedesk.js): la trimis butonul se stinge, ca in portal, ca sa nu plece
 * de doua ori acelasi email.
 */
function SendAgentLinkForm({ ticket, owner, size }: { ticket: number; owner: number; size: 'xs' | 'sm' }) {
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  return (
    <form
      className="tk-rconnect-form"
      method="post"
      action="/api/v2/remote-connect/agent-link"
      onSubmit={(ev) => {
        ev.preventDefault()
        setState('sending')
        sendAgentDownloadLink(ticket, owner)
          .then(() => {
            toast('Downoad link sent', 'Download agent')
            setState('sent')
          })
          .catch((err: unknown) => {
            toast(err instanceof Error ? err.message : 'Error', 'Download agent', 'error')
            setState('idle')
          })
      }}
    >
      <input type="hidden" name="ticket" value={ticket} />
      <input type="hidden" name="owner" value={owner} />
      <button className={`btn btn-primary btn-${size}`} type="submit" disabled={state !== 'idle'}>
        Send agent download link by email
      </button>
    </form>
  )
}

function DeviceIdentity({ device }: { device: TicketRemoteDevice }) {
  return (
    <table className="tk-rconnect-id">
      <tbody>
        <tr>
          <td rowSpan={2} className="tk-rconnect-mark">
            <StatusDot lastActivity={device.lastActivity} />
            <TypeIcon type={device.type} />
          </td>
          <td className="tk-rconnect-name">{device.name}</td>
        </tr>
        <tr>
          <td className="tk-rconnect-logged">
            Logged user: <span className="tk-rconnect-user">{device.loggedUser}</span>
          </td>
        </tr>
      </tbody>
    </table>
  )
}

function RemoteDevice({ ticket, device }: { ticket: number; device: TicketRemoteDevice }) {
  /*
   * Cu RustDesk, nici "not in the office" nici bifa de consimtamant nu mai
   * inseamna nimic: releul trece oriunde ar fi statia, iar consimtamantul se
   * judeca pe pagina de lansare. Aceeasi hotarare ca in macheta veche pe
   * RustDesk; ramane doar semnul "in the office".
   */
  return (
    <div className="bs-callout bs-callout-info bs-callout-sm mt-0 rmt-device tk-rconnect-device">
      <div className="row">
        <div className="col-md-6">
          <DeviceIdentity device={device} />
        </div>

        <div className="col-md-3 align-right">
          {/*
           * Butonul duce la pagina de lansare - mutata, la aceeasi adresa ca in
           * portal - in fila noua, cum deschidea portalul `hostUrlJoin`.
           */}
          <a
            id={`connect_to_${device.id}`}
            className="btn btn-sm btn-success tk-rconnect-rustdesk"
            href={rustdeskLaunchUrl(device.id, ticket)}
            target="_blank"
            rel="noreferrer"
            data-device={device.id}
            data-ticket={ticket}
          >
            <i className="fas fa-desktop" aria-hidden="true"></i> Create session
          </a>
        </div>

        {device.inOffice && (
          <div className="col-md-8">
            <div className="alert alert-info rmt-note tk-rconnect-alert" role="alert">
              <i className="fas fa-exclamation-circle" aria-hidden="true"></i> This device is in the office
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function LinuxDevice({ device }: { device: TicketRemoteLinuxDevice }) {
  const [info, setInfo] = useState(false)
  /* Un server Linux n-are agent, deci nu are nici "utilizator logat":
     se leaga dupa adresa, si aia e singurul lucru care ajuta. */
  return (
    <div className="note note-info tk-rconnect-note rmt-device tk-rconnect-linux">
      <a href={`ssh://${device.ip}`} className="btn btn-primary">
        Connect to {device.ip}
      </a>{' '}
      <button type="button" className="puttyInformation" onClick={() => setInfo(true)}>
        <i className="fas fa-info-circle" aria-hidden="true"></i> Information
      </button>

      <Modal
        id="puttyInformationModal"
        open={info}
        onClose={() => setInfo(false)}
        title="Setup information"
        labelledBy="puttyInformationModalLabel"
        footer={
          <button type="button" className="btn btn-default" onClick={() => setInfo(false)}>
            Close
          </button>
        }
      >
        1.{' '}
        <a href={remoteDownloadUrl('putty')} target="_blank" rel="noreferrer">
          Click here to download the archive
        </a>
        <br />
        <br />
        2. Unzip the archive in c:\Program Files (x86)\ (please make sure the path to putty is c:\Program Files
        (x86)\PuTTY\putty.exe)
        <br />
        <br />
        3. Open register-putty.reg from c:\Program Files (x86)\PuTTY\ to edit the register and confirm the next prompts
        (You will need to have admin access)
        <br />
        <br />
        <br />
        To uninstall just run unregister-putty.reg
      </Modal>
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/*  Remote Control                                                           */
/* ------------------------------------------------------------------------ */

export function TicketRemoteControl({ id }: { id: number }) {
  const { data, error } = useQuery(ticketRemoteControlQuery(id))
  const [chosen, setChosen] = useState<{ device: number; tab: string } | null>(null)

  /* Hook-urile stau INAINTE de orice `return` de mai jos: la primul desen
     (fara date) React numara hook-urile, si un `useQuery` chemat abia dupa
     ce vin datele face numarul altul - "Rendered more hooks than during the
     previous render", si panoul ramane gol. Asa a stat de la 806199e31. */
  const device = chosen?.device ?? data?.devices[0]?.id ?? 0
  const unviewed = useQuery({
    ...rcUnviewedQuery(id, device),
    enabled: device > 0 && (data?.tabs.some((one) => one.id === 'events_tab') ?? false),
  }).data?.unviewed ?? 0

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  /*
   * Fila deschisa la inceput e cea cu numele subcategoriei tichetului - asa
   * potrivea si macheta veche, fara sa tina seama de litere mari sau mici. Daca
   * nu se potriveste niciuna, `InitRemoteControl` apasa singur pe prima.
   */
  const start = data.tabs.find((tab) => tab.label.toLowerCase() === data.subcategory.toLowerCase())
  const tab = chosen?.tab ?? start?.href ?? data.tabs[0]?.href ?? ''

  /*
   * Aceeasi asezare ca `remote_control/index.php`: lista de file pe doua
   * coloane, intr-o cutie cu derulare (slimScroll, 600px), iar in dreapta
   * alegerea masinii - un buton albastru de selectpicker, jumatate de rand,
   * tras la dreapta - cu panoul dedesubt.
   */
  return (
    <div className={`row remote-control-data-${id} tk-rcontrol`}>
      <div className="col-md-2">
        <div className="scroll-container tk-rcontrol-nav">
          <ul className="nav nav-metro-tabs tabs-left rmt-tabs">
            {data.tabs.map((one) => (
              <li key={one.id || one.href} className={tab === one.href ? 'active' : undefined}>
                <a
                  href={`/${one.href}${id}`}
                  id={one.id ? `${one.id}_${id}` : undefined}
                  onClick={(ev) => {
                    ev.preventDefault()
                    setChosen({ device, tab: one.href })
                  }}
                >
                  {one.label}
                  {one.id === 'events_tab' && (
                    <span className="badge tk-rcontrol-badge" id="events_badge">
                      {unviewed}
                    </span>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="col-md-10">
        {data.devices.length === 0 ? (
          <div className="row">
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-circle" aria-hidden="true"></i> No device available
            </div>
          </div>
        ) : (
          <>
            <div className="col-md-6 pull-right tk-rcontrol-pick-wrap">
              <select
                className="form-control input-xs rmt-pick tk-rcontrol-pick"
                name={`selected_device_${id}`}
                value={device}
                onChange={(ev) => setChosen({ device: Number(ev.target.value), tab })}
              >
                {data.devices.map((one) => {
                  const ago = agoOrEmpty(one.lastActivity)
                  return (
                    <option key={one.id} value={one.id}>
                      {ago ? `${one.name} (${ago})` : one.name}
                    </option>
                  )
                })}
              </select>
            </div>

            <div className={`nav-content-${id} rmt-panel tk-rcontrol-content`}>
              {tab === '' ? (
                <div className="col-md-12">
                  <div className="alert alert-info" role="alert">
                    <i className="fas fa-info-circle" aria-hidden="true"></i> There are no commands available.
                  </div>
                </div>
              ) : (
                /*
                 * Panourile din stanga sunt chiar ecranele fisei de aparat, pe
                 * cheia lor din meniu - deci se deseneaza aici, pe masina aleasa.
                 * `renderScreen` trimite singur la portal ce nu s-a mutat inca.
                 */
                renderScreen(tab, data.tabs.find((one) => one.href === tab)?.label ?? '', {
                  id: device,
                  from: 'easeedesk',
                  overview: [],
                  open: () => undefined,
                  ticket: id,
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
